/* ============================================================
   Adam Decor · Interactive Effects
   v1.0 · 2026-08-04
   Requires: anime.js 3.2.2 (loaded in theme.liquid)
   ============================================================ */

(function () {
  'use strict';

  // ═══════════════════════════════════════════════
  // 1. Scroll-Triggered Reveal (Intersection Observer)
  // ═══════════════════════════════════════════════

  function initScrollReveal() {
    const revealTargets = document.querySelectorAll(
      '.card-wrapper, .collection-card-wrapper, .section-heading, .collection__title, .rich-text__heading, .banner__heading'
    );

    if (!revealTargets.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target;
            const delay = el.dataset.revealDelay || 0;
            const index = Array.from(el.parentNode?.children || []).indexOf(el);

            // Stagger cards in a grid
            const stagger = el.closest('.grid')
              ? index * 80
              : delay;

            setTimeout(() => {
              anime({
                targets: el,
                opacity: [0, 1],
                translateY: [30, 0],
                duration: 600,
                easing: 'easeOutCubic',
              });
            }, stagger);

            observer.unobserve(el);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -40px 0px',
      }
    );

    revealTargets.forEach((el) => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(30px)';
      observer.observe(el);
    });
  }

  // ═══════════════════════════════════════════════
  // 2. Product Card 3D Tilt
  // ═══════════════════════════════════════════════

  function initCardTilt() {
    const cards = document.querySelectorAll('.card-wrapper');

    cards.forEach((card) => {
      card.addEventListener('mousemove', (e) => {
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;

        // Subtle rotation — max 5deg
        const rotateX = ((y - centerY) / centerY) * -3;
        const rotateY = ((x - centerX) / centerX) * 5;

        anime({
          targets: card,
          rotateX: rotateX,
          rotateY: rotateY,
          scale: 1.02,
          duration: 400,
          easing: 'easeOutQuad',
        });
      });

      card.addEventListener('mouseleave', () => {
        anime({
          targets: card,
          rotateX: 0,
          rotateY: 0,
          scale: 1,
          duration: 500,
          easing: 'easeOutElastic(1, .6)',
        });
      });
    });
  }

  // ═══════════════════════════════════════════════
  // 3. Gold Glow Pulse — Buttons + CTA
  // ═══════════════════════════════════════════════

  function initButtonGlow() {
    const buttons = document.querySelectorAll(
      '.button--primary, .product-form__submit, .shopify-payment-button__button'
    );

    buttons.forEach((btn) => {
      btn.addEventListener('mouseenter', () => {
        anime({
          targets: btn,
          boxShadow: [
            '0 0 0 0 rgba(200,169,110,0)',
            '0 0 20px 4px rgba(200,169,110,0.35)',
          ],
          duration: 400,
          easing: 'easeOutSine',
        });
      });

      btn.addEventListener('mouseleave', () => {
        anime({
          targets: btn,
          boxShadow: [
            '0 0 20px 4px rgba(200,169,110,0.35)',
            '0 0 0 0 rgba(200,169,110,0)',
          ],
          duration: 600,
          easing: 'easeOutSine',
        });
      });
    });
  }

  // ═══════════════════════════════════════════════
  // 4. Click Ripple Effect
  // ═══════════════════════════════════════════════

  function initClickRipple() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.button, .product-form__submit');
      if (!btn) return;

      // Prevent double ripples
      if (btn.querySelector('.adam-ripple')) return;

      const rect = btn.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height);
      const x = e.clientX - rect.left - size / 2;
      const y = e.clientY - rect.top - size / 2;

      const ripple = document.createElement('span');
      ripple.className = 'adam-ripple';
      ripple.style.cssText = `
        position: absolute;
        width: ${size}px;
        height: ${size}px;
        left: ${x}px;
        top: ${y}px;
        border-radius: 50%;
        background: rgba(200,169,110,0.3);
        pointer-events: none;
      `;
      btn.style.position = btn.style.position || 'relative';
      btn.style.overflow = 'hidden';
      btn.appendChild(ripple);

      anime({
        targets: ripple,
        scale: [0, 2],
        opacity: [1, 0],
        duration: 600,
        easing: 'easeOutExpo',
        complete: () => ripple.remove(),
      });
    });
  }

  // ═══════════════════════════════════════════════
  // 5. Hero Banner — Subtle Parallax Zoom
  // ═══════════════════════════════════════════════

  function initHeroParallax() {
    const banner = document.querySelector('.banner__media img');
    if (!banner) return;

    let ticking = false;

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollY = window.scrollY;
          if (scrollY < window.innerHeight) {
            const scale = 1 + scrollY * 0.0003;
            const opacity = 1 - scrollY * 0.001;
            banner.style.transform = `scale(${Math.min(scale, 1.15)})`;
            banner.style.opacity = Math.max(opacity, 0.5);
          }
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  // ═══════════════════════════════════════════════
  // 6. Navigation — Shrink On Scroll
  // ═══════════════════════════════════════════════

  function initNavShrink() {
    const header = document.querySelector('.header-wrapper, sticky-header');
    if (!header) return;

    let lastScroll = 0;
    let ticking = false;

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollY = window.scrollY;

          if (scrollY > 80) {
            header.style.padding = '8px 0';
            header.style.transition = 'padding 0.3s ease, background 0.3s ease';
            header.style.background = 'rgba(10,10,10,0.95)';
          } else {
            header.style.padding = '16px 0';
            header.style.background = 'rgba(10,10,10,0.75)';
          }

          lastScroll = scrollY;
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  // ═══════════════════════════════════════════════
  // 7. Navigation Dropdown — Smooth Reveal
  // ═══════════════════════════════════════════════

  function initDropdownReveal() {
    const dropdowns = document.querySelectorAll(
      '.header__menu-item[aria-haspopup], .mega-menu__trigger, details.header__menu-item'
    );

    dropdowns.forEach((trigger) => {
      const target = trigger.querySelector('.mega-menu, .header__submenu, ul');
      if (!target) return;

      trigger.addEventListener('mouseenter', () => {
        anime({
          targets: target,
          opacity: [0, 1],
          translateY: [8, 0],
          duration: 250,
          easing: 'easeOutCubic',
          begin: () => { target.style.display = 'block'; },
        });
      });

      trigger.addEventListener('mouseleave', () => {
        anime({
          targets: target,
          opacity: [1, 0],
          translateY: [0, 6],
          duration: 180,
          easing: 'easeInCubic',
          complete: () => { target.style.display = 'none'; },
        });
      });
    });
  }

  // ═══════════════════════════════════════════════
  // 8. Scroll To Top Button
  // ═══════════════════════════════════════════════

  function initScrollToTop() {
    // Create button
    const btn = document.createElement('button');
    btn.id = 'adam-scroll-top';
    btn.setAttribute('aria-label', 'Back to top');
    btn.innerHTML = '↑';
    btn.style.cssText = `
      position: fixed;
      bottom: 30px;
      right: 30px;
      z-index: 999;
      width: 44px;
      height: 44px;
      border-radius: 50%;
      background: #C8A96E;
      color: #0A0A0A;
      border: none;
      font-size: 20px;
      cursor: pointer;
      opacity: 0;
      transform: translateY(20px);
      pointer-events: none;
      transition: none;
    `;
    document.body.appendChild(btn);

    let ticking = false;

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const show = window.scrollY > 500;
          anime({
            targets: btn,
            opacity: show ? [0, 1] : [1, 0],
            translateY: show ? [20, 0] : [0, 20],
            duration: 300,
            easing: 'easeOutCubic',
            begin: () => {
              btn.style.pointerEvents = show ? 'auto' : 'none';
            },
          });
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    btn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });

    // Hover animation
    btn.addEventListener('mouseenter', () => {
      anime({
        targets: btn,
        scale: 1.1,
        boxShadow: '0 4px 20px rgba(200,169,110,0.5)',
        duration: 250,
        easing: 'easeOutQuad',
      });
    });

    btn.addEventListener('mouseleave', () => {
      anime({
        targets: btn,
        scale: 1,
        boxShadow: '0 2px 8px rgba(200,169,110,0.2)',
        duration: 250,
        easing: 'easeOutQuad',
      });
    });
  }

  // ═══════════════════════════════════════════════
  // 9. Image Lazy Load — Blur-Up Reveal
  // ═══════════════════════════════════════════════

  function initImageReveal() {
    // T3-39 修复（2026-09-21）：入场动画只碰「主图」，跳过 .media--hover-effect 里的图。
    // 原因：这里会给每张 <img> 写内联 opacity/transform，内联样式优先级高于类选择器，
    // 会盖掉 base.css 的 `.media.media--hover-effect>img+img{opacity:0}`（第 2 张图默认隐藏）
    // 与 component-card.css 的悬停换图规则 → Amazon 第 2 张「尺寸/数量图」永远压在主图上。
    const images = Array.from(
      document.querySelectorAll(
        '.card__media img, .product__media img, .collection-card img'
      )
    ).filter(
      (img) =>
        !(img.parentElement && img.parentElement.classList.contains('media--hover-effect'))
    );

    if (!images.length) return;

    const imgObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.revealed) return;
            img.dataset.revealed = 'true';

            img.style.transition = 'opacity 0.5s ease, transform 0.6s ease';
            img.style.opacity = '0';
            img.style.transform = 'scale(1.05)';

            if (img.complete) {
              reveal(img);
            } else {
              img.addEventListener('load', () => reveal(img), { once: true });
            }

            imgObserver.unobserve(img);
          }
        });
      },
      { threshold: 0.05 }
    );

    function reveal(img) {
      requestAnimationFrame(() => {
        img.style.opacity = '1';
        img.style.transform = 'scale(1)';
      });
    }

    images.forEach((img) => imgObserver.observe(img));
  }

  // ═══════════════════════════════════════════════
  // 10. Collection Card — Gold Line Sweep On Hover
  // ═══════════════════════════════════════════════

  function initCollectionHover() {
    const cards = document.querySelectorAll('.collection-card-wrapper .card, .collection-list .card');

    cards.forEach((card) => {
      // Inject a gold line overlay if not exists
      if (!card.querySelector('.adam-gold-line')) {
        const line = document.createElement('div');
        line.className = 'adam-gold-line';
        line.style.cssText = `
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 2px;
          background: #C8A96E;
          transform: scaleX(0);
          transform-origin: left;
          transition: none;
          z-index: 2;
        `;
        card.style.position = card.style.position || 'relative';
        card.appendChild(line);
      }

      const line = card.querySelector('.adam-gold-line');

      card.addEventListener('mouseenter', () => {
        anime({
          targets: card,
          scale: 1.02,
          duration: 300,
          easing: 'easeOutQuad',
        });
        anime({
          targets: line,
          scaleX: [0, 1],
          duration: 400,
          easing: 'easeOutCubic',
        });
      });

      card.addEventListener('mouseleave', () => {
        anime({
          targets: card,
          scale: 1,
          duration: 300,
          easing: 'easeOutQuad',
        });
        anime({
          targets: line,
          scaleX: [1, 0],
          duration: 300,
          easing: 'easeInCubic',
        });
      });
    });
  }

  // ═══════════════════════════════════════════════
  // 11. Price Animation — Subtle Pulse On Change
  // ═══════════════════════════════════════════════

  function initPricePulse() {
    const prices = document.querySelectorAll('.price-item--sale, .price--on-sale .price-item--sale');
    prices.forEach((price) => {
      anime({
        targets: price,
        scale: [0.95, 1],
        opacity: [0.6, 1],
        duration: 500,
        easing: 'easeOutBack',
        delay: anime.stagger(60),
      });
    });
  }

  // ═══════════════════════════════════════════════
  // Init — Run on DOM ready
  // ═══════════════════════════════════════════════

  function init() {
    initScrollReveal();
    initCardTilt();
    initButtonGlow();
    initClickRipple();
    initHeroParallax();
    initNavShrink();
    initDropdownReveal();
    initScrollToTop();
    initImageReveal();
    initCollectionHover();
    initPricePulse();
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run scroll reveal when Shopify theme editor changes sections
  document.addEventListener('shopify:section:load', () => {
    setTimeout(initScrollReveal, 200);
    setTimeout(initImageReveal, 200);
  });

})();
